import { getAddress, isAddress, zeroAddress, type Address } from "viem";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const REFERRAL_STORAGE_KEY = "fengniao-ref";
export const REFERRAL_REWARD_BPS = 200; // 2%
export const REFERRAL_HOLD_HOURS = 48;
export const REFERRAL_MIN_BUY_LABEL = "100,000 蜂鸟";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function hasLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function toChecksumOrNull(value: string): Address | null {
  if (!value || !isAddress(value)) return null;
  const checksum = getAddress(value);
  if (checksum === zeroAddress) return null;
  return checksum;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Read `?ref=<address>` from a URL string, validate, and return checksummed Address or null. */
export function readReferrerFromUrl(href: string): Address | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const raw = url.searchParams.get("ref");
  if (!raw) return null;
  return toChecksumOrNull(raw);
}

/**
 * Persist a referrer to localStorage.
 *
 * Returns true on success, false when the input is malformed, the zero
 * address, or matches `self` (self-referral). Comparisons are case-insensitive.
 */
export function storeReferrer(referrer: string, self?: string): boolean {
  const checksum = toChecksumOrNull(referrer);
  if (!checksum) return false;

  if (self) {
    const selfChecksum = toChecksumOrNull(self);
    if (selfChecksum && checksum.toLowerCase() === selfChecksum.toLowerCase()) {
      return false;
    }
  }

  if (!hasLocalStorage()) return false;
  try {
    window.localStorage.setItem(REFERRAL_STORAGE_KEY, checksum);
    return true;
  } catch {
    return false;
  }
}

/** Read the stored referrer; returns null if missing or corrupted. */
export function getStoredReferrer(): Address | null {
  if (!hasLocalStorage()) return null;
  try {
    const raw = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return null;
    return toChecksumOrNull(raw);
  } catch {
    return null;
  }
}

/** Read the stored referrer; falls back to zeroAddress for callers that need a non-null. */
export function getStoredReferrerOrZero(): Address {
  return getStoredReferrer() ?? zeroAddress;
}

/** Build the shareable invite URL for the connected wallet. */
export function buildInviteLink(origin: string, inviter: Address): string {
  const cleanOrigin = origin.replace(/\/$/, "");
  return `${cleanOrigin}/?ref=${inviter}`;
}

/** Bind message used for signing — single source of truth shared with the keeper. */
export function buildBindMessage(invitee: Address, inviter: Address): string {
  return `Fengniao Referral Bind\nInvitee: ${invitee.toLowerCase()}\nInviter: ${inviter.toLowerCase()}`;
}

/** Claim message used for signing; timestamp in seconds. */
export function buildClaimMessage(address: Address, timestamp: number): string {
  return `Fengniao Referral Claim\nAddress: ${address.toLowerCase()}\nTimestamp: ${timestamp}`;
}

// ---------------------------------------------------------------------------
// UI copy (single source of truth for referral terminology)
// ---------------------------------------------------------------------------

export const REFERRAL_COPY = {
  inviterLabel: "邀请人",
  inviteeLabel: "被邀请人",
  rewardLine: "邀请奖励为被邀请人有效买入量的 2%",
  requirementLine: `被邀请人单笔买入不少于 ${REFERRAL_MIN_BUY_LABEL}，并持币 ${REFERRAL_HOLD_HOURS} 小时后，邀请人可领取奖励`,
  notStakingLine: "邀请奖励来自项目专属钱包，不影响代币税收分配",
} as const;
